import type { Task } from "../data/blocks";
import { getCatColor, PILLAR_META } from "../data/pillars";
import { T } from "../styles/theme";
import type { CheckedMap } from "../types";
import { Checkmark } from "./Checkmark";
import { HairWashTracker } from "./HairWashTracker";

interface TimelineViewProps {
  tasks: Task[];
  checked: CheckedMap;
  onToggle: (id: string) => void;
  hairLastDone: string | null;
  setHairLastDone: (next: string | null) => void;
  hairSkipped: boolean;
  setHairSkipped: (next: boolean) => void;
  hairEditOpen: boolean;
  setHairEditOpen: (next: boolean) => void;
}

/** Time-ordered list of the day's tasks with time-group headers. */
export function TimelineView({
  tasks,
  checked,
  onToggle,
  hairLastDone,
  setHairLastDone,
  hairSkipped,
  setHairSkipped,
  hairEditOpen,
  setHairEditOpen,
}: TimelineViewProps) {
  return (
    <div
      style={{
        background: T.white,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 2px 14px rgba(45,32,22,0.07)",
        border: `1px solid ${T.parchment}`,
      }}
    >
      {tasks.map((task, i) => {
        const id = task.id ?? "";
        const isDone = !!checked[id];
        const color = getCatColor(task.cat);
        const showTime = i === 0 || tasks[i - 1].time !== task.time;
        return (
          <div key={id}>
            {showTime && (
              <div
                style={{
                  padding: "7px 16px 3px",
                  fontSize: 9,
                  fontWeight: 700,
                  color: T.terra,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  background: T.parchment,
                  borderTop: i === 0 ? "none" : `1px solid ${T.parchment}`,
                }}
              >
                {task.time}
              </div>
            )}
            <div
              onClick={() => onToggle(id)}
              style={{
                display: "flex",
                alignItems: "flex-start",
                padding: "11px 16px 11px 13px",
                gap: 11,
                cursor: "pointer",
                borderLeft: `3px solid ${isDone ? T.sage : color}`,
                background: isDone ? "#f8fdf6" : T.white,
                transition: "all 0.2s",
                borderBottom: i < tasks.length - 1 ? `1px solid ${T.cream}` : "none",
              }}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  flexShrink: 0,
                  marginTop: 1,
                  border: isDone ? "none" : `2px solid ${color}`,
                  background: isDone ? T.sage : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                }}
              >
                {isDone && <Checkmark />}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    color: isDone ? T.sageLight : T.ink,
                    textDecoration: isDone ? "line-through" : "none",
                    transition: "all 0.2s",
                    lineHeight: 1.45,
                  }}
                >
                  {task.label}
                </div>
                {task.trackId === "hairwash" && (
                  <HairWashTracker
                    hairLastDone={hairLastDone}
                    setHairLastDone={setHairLastDone}
                    hairSkipped={hairSkipped}
                    setHairSkipped={setHairSkipped}
                    hairEditOpen={hairEditOpen}
                    setHairEditOpen={setHairEditOpen}
                    onMarkTaskComplete={() => onToggle(id)}
                  />
                )}
                <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                  <span
                    style={{
                      fontSize: 9,
                      color,
                      background: PILLAR_META[task.cat]?.bg ?? T.parchment,
                      borderRadius: 6,
                      padding: "2px 7px",
                      fontWeight: 600,
                    }}
                  >
                    {task.cat}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      color: T.inkLight,
                      background: T.parchment,
                      borderRadius: 6,
                      padding: "2px 7px",
                      fontStyle: "italic",
                    }}
                  >
                    ⏱ {task.duration}
                  </span>
                  {task.personal && (
                    <span
                      style={{
                        fontSize: 9,
                        color: T.terra,
                        background: `${T.terra}15`,
                        borderRadius: 6,
                        padding: "2px 7px",
                      }}
                    >
                      🌟 yours
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
